// KREIEREN-eigener API-Client (kreieren-api :9300 via Auth-Gateway).
// ⚠️ EIGENE DATEI mit Absicht: morpheusClient.js wird parallel von der Morpheus-Seite
// generiert/überschrieben — am 03.07.2026 flogen die Kreieren-Methoden dort 3× raus
// (einmal war die Seite down). Alles Kreieren-spezifische lebt deshalb HIER.
// Shared-Endpoints (run/reels/calendar/schedule-draft/dropbox) bleiben in morpheusClient.
import { getToken, logout } from "./auth";

const API_URL = "https://api.morpheus.maatz.studio";

function authHeaders(extra = {}) {
  const t = getToken();
  return { ...extra, ...(t ? { Authorization: `Bearer ${t}` } : {}) };
}

async function handle(res) {
  if (res.status === 401) {
    logout();
    throw new Error("Session abgelaufen – bitte neu einloggen.");
  }
  if (!res.ok) throw new Error(`API error: ${res.status}`);
  return res.json();
}

export const kreieren = {
  // Streams (Content-Säulen aus streams.json — NICHT das alte /streams = Musik-Zahlen)
  async getContentStreams() {
    const res = await fetch(`${API_URL}/social/streams`, { headers: authHeaders() });
    return handle(res);
  },
  async getLearnings() {
    const res = await fetch(`${API_URL}/social/learnings`, { headers: authHeaders() });
    return handle(res);
  },
  // Repertoire (Musik-Welt): Song-Katalog aus Supabase songs + songs_roba (eine Wahrheit, read-only)
  async getRepertoire() {
    const res = await fetch(`${API_URL}/social/repertoire`, { headers: authHeaders() });
    return handle(res);
  },
  // Musik-Create: Suno-Guthaben (sunoapi.org, read-only)
  async getSunoCredits() {
    const res = await fetch(`${API_URL}/social/suno-credits`, { headers: authHeaders() });
    return handle(res);
  },
  // Musik-Create: Verlags-Briefings von Spider (geteilte briefs-Tabelle, read-only)
  async getBriefs() {
    const res = await fetch(`${API_URL}/social/briefs`, { headers: authHeaders() });
    return handle(res);
  },
  // Musik-Create: Pitch-Ordner-Spiegel aus Dropbox (Versionen + Stems pro Pitch)
  async getPitches() {
    const res = await fetch(`${API_URL}/social/pitches`, { headers: authHeaders() });
    return handle(res);
  },
  // Trend-Radar: gesammelte Trend-Karten (ai_memory trend:content:*), neueste zuerst.
  async getTrends() {
    const res = await fetch(`${API_URL}/social/trends`, { headers: authHeaders() });
    return handle(res);
  },
  // Löst einen echten Niche-Scan aus (scan_niche_trends → Gemini → speichert Karten). Background.
  async scanTrends() {
    const res = await fetch(`${API_URL}/social/trends/scan`, { method: "POST", headers: authHeaders() });
    return handle(res);
  },
  async getDoc(path) {
    const res = await fetch(`${API_URL}/social/doc?path=${encodeURIComponent(path)}`, { headers: authHeaders() });
    return handle(res);
  },
  // Text-Docs eines Dropbox-Ordners listen (Werkbank-Scripts-Tab: raw_path + "/scripts")
  async listDocs(path) {
    const res = await fetch(`${API_URL}/social/docs?path=${encodeURIComponent(path)}`, { headers: authHeaders() });
    return handle(res);
  },
  // Akten = Studio/<reel_id>/-Ordner (Thread = reel_id = Ordner) — für „Neuer Auftrag"
  async listAkten() {
    const res = await fetch(`${API_URL}/social/akten`, { headers: authHeaders() });
    return handle(res);
  },
  // Docs, die der Agent auto-lädt (Format Vorlagen Top-Level-.md: BRAND_BELIEFS, HOOK_SKILL, …)
  async getBrandDocs() {
    const res = await fetch(`${API_URL}/social/brand-docs`, { headers: authHeaders() });
    return handle(res);
  },
  // Steht `gewollt` (JSON-Wert) so in der Datei? true / false / null (nicht lesbar). Nach einem Schreibfehler (14.10.2026).
  async stehtAufPlatte(path, gewollt) {
    try {
      const z = await this.getDoc(path);
      return z?.success ? JSON.stringify(JSON.parse(z.text)) === JSON.stringify(gewollt) : null;
    } catch { return null; }
  },
  async saveDoc(path, text) {
    const res = await fetch(`${API_URL}/social/doc`, {
      method: "PUT",
      headers: authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify({ path, text }),
    });
    return handle(res);
  },
  // Mattis Liedtext-Zeile (Spec Songtext-Karte §6.4): dieselbe Caption-Tür wie der Agent.
  async liedtext(reelId, text) {
    const res = await fetch(`${API_URL}/social/liedtext`, {
      method: "POST",
      headers: authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify({ reel_id: reelId, text }),
    });
    // Gateway (morpheus-auth) answers 504/502 with {success:false, message} — show his reason, not the code.
    if (res.status === 504 || res.status === 502) {
      const b = await res.json().catch(() => null);
      if (b && (b.message || b.error)) return b;
    }
    return handle(res);
  },
  async getFormat(key) {
    const res = await fetch(`${API_URL}/social/format/${encodeURIComponent(key)}`, { headers: authHeaders() });
    return handle(res);
  },
  // Material-Lage pro Format (Slice-2-Abgleich): Clips, deren moment_candidates das Format vorschlagen
  async getMaterial(formatKey) {
    const res = await fetch(`${API_URL}/social/material?format=${encodeURIComponent(formatKey)}`, { headers: authHeaders() });
    return handle(res);
  },
  // Das Lager (25.09.2026): Stückliste · Bestand · Einkaufsliste je Format — dieselbe Logik wie `lies_lager`.
  async getLager(formatKey = "", reels = 0) {
    const q = formatKey ? `?format=${encodeURIComponent(formatKey)}&reels=${encodeURIComponent(reels)}` : "";
    const res = await fetch(`${API_URL}/social/lager${q}`, { headers: authHeaders() });
    return handle(res);
  },
  async saveStueckliste(key, teile, status = "entwurf") {
    const res = await fetch(`${API_URL}/social/lager/${encodeURIComponent(key)}/stueckliste`, {
      method: "PUT", headers: authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify({ teile, status }),
    });
    return handle(res);
  },
  async saveFormat(key, data) {
    const res = await fetch(`${API_URL}/social/format/${encodeURIComponent(key)}`, {
      method: "PUT",
      headers: authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify(data),
    });
    return handle(res);
  },
  // Server-Fonts für die Hook-Maske: Liste + Datei (als ArrayBuffer für FontFace —
  // @font-face-URLs können keine Auth-Header schicken, fetch+FontFace kann)
  async getFonts() {
    const res = await fetch(`${API_URL}/social/fonts`, { headers: authHeaders() });
    return handle(res);
  },
  async fontData(file) {
    const res = await fetch(`${API_URL}/social/font/${encodeURIComponent(file)}`, { headers: authHeaders() });
    if (!res.ok) throw new Error(`Font ${file}: ${res.status}`);
    return res.arrayBuffer();
  },
  // Song-Profile: zweite Achse neben den Formaten (Songs/<key>/song.json, campaigns.<format_key>)
  async getSongs() {
    const res = await fetch(`${API_URL}/social/songs`, { headers: authHeaders() });
    return handle(res);
  },
  async getSong(key) {
    const res = await fetch(`${API_URL}/social/song/${encodeURIComponent(key)}`, { headers: authHeaders() });
    return handle(res);
  },
  async saveSong(key, data) {
    const res = await fetch(`${API_URL}/social/song/${encodeURIComponent(key)}`, {
      method: "PUT",
      headers: authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify(data),
    });
    return handle(res);
  },
  async uploadCut(file, formatKey = "", destDir = "") {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("format_key", formatKey);
    if (destDir) fd.append("dest_dir", destDir);
    const res = await fetch(`${API_URL}/social/upload-cut`, { method: "POST", headers: authHeaders(), body: fd });
    return handle(res);
  },
  // Plan-Board: echte Assets aus /Content/<brand>/Social (Drafts/Scheduled/Posted)
  async getAssets(brandRoot = "/Content/FUNKENSTEIN") {
    const res = await fetch(`${API_URL}/social/assets?brand_root=${encodeURIComponent(brandRoot)}`, { headers: authHeaders() });
    return handle(res);
  },
  // Für <img src> — Header gehen nicht, Gateway akzeptiert ?token=
  thumbUrl(path, size = "w256h256") {
    return `${API_URL}/social/thumb?path=${encodeURIComponent(path)}&size=${size}&token=${encodeURIComponent(getToken() || "")}`;
  },
  // Hand-Eingriff mechanisch in den Lern-Topf (reel_intervention) — feuert beim
  // Speichern in Timing-Leiste/Hook-Maske, unabhängig vom Chat-Abschicken
  async postIntervention(payload) {
    const res = await fetch(`${API_URL}/social/intervention`, { method: "POST", headers: authHeaders({ "Content-Type": "application/json" }), body: JSON.stringify(payload) });
    return handle(res);
  },
  // Base-Render (Schnitt+Ton OHNE Captions) für die Hook-Maske — <video src>.
  // `frisch` (15.09.2026): ein Schluessel je Fassung. Sonst hat jeder Render dieselbe Adresse,
  // und der Browser hielte nach v9 das Base von v8 fest.
  reelBaseUrl(reelId, frisch) {
    const f = frisch ? `&frisch=${encodeURIComponent(frisch)}` : "";
    return `${API_URL}/social/reel-base?reel_id=${encodeURIComponent(reelId)}${f}&token=${encodeURIComponent(getToken() || "")}`;
  },
  // 720p-Proxy eines Source-Clips (Timing-Leiste): 4K-Originale = Hänger.
  // Nur nutzen, wenn clipProxyInfo lokal:true sagt — sonst Original.
  clipProxyUrl(path) {
    return `${API_URL}/social/clip-proxy?path=${encodeURIComponent(path)}&token=${encodeURIComponent(getToken() || "")}`;
  },
  async clipProxyInfo(paths) {
    const res = await fetch(`${API_URL}/social/clip-proxy-info`, {
      method: "POST",
      headers: authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify({ paths }),
    });
    return handle(res);
  },
  // Asset → Social/{format}/Archived/ (Cron löscht dort nach 30 Tagen endgültig)
  // Akte verwerfen (26.08.): akte.json.status = "verworfen" → Karte weg aus der Galerie.
  async akteVerwerfen(id) {
    const res = await fetch(`${API_URL}/social/akte-verwerfen`, {
      method: "POST",
      headers: authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify({ id }),
    });
    return handle(res);
  },
  // Mattis ❌ auf einer Kachel (03.09.): „auch fehlerhafte müssen aufgeräumt werden
  // können." Social-Assets gehen ins Archiv, Akte-Fassungen und Fotos in einen
  // Verworfen-Ordner NEBEN der Datei. Getrennt von archiveAsset, weil DAS der
  // Freigabe-Weg ist — der lässt Akte-Fassungen bewusst liegen (Historie des Reels).
  async assetVerwerfen(path) {
    const res = await fetch(`${API_URL}/social/asset-verwerfen`, {
      method: "POST",
      headers: authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify({ path }),
    });
    return handle(res);
  },
  async archiveAsset(path) {
    const res = await fetch(`${API_URL}/social/asset-archive`, {
      method: "POST",
      headers: authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify({ path }),
    });
    return handle(res);
  },
  // Mattis Freigabe (02.08.): Asset → Social/…/Approved/ — der Planungs-Pool.
  // Das Plan-Board bietet zum Planen nur Approved an; der Ordner IST der Status.
  async approveAsset(path) {
    const res = await fetch(`${API_URL}/social/asset-approve`, {
      method: "POST",
      headers: authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify({ path }),
    });
    return handle(res);
  },
  // Rückweg (Texthook 03.08.): Approved-Asset zurück nach Drafts — aufs
  // Werkbank-Niveau holen, z. B. für neue Text-Hook-Varianten.
  async returnAsset(path) {
    const res = await fetch(`${API_URL}/social/asset-return`, {
      method: "POST",
      headers: authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify({ path }),
    });
    return handle(res);
  },
  // Tages-Puls: Kalender-Slots × wirklich-gepostet (auto_poster) pro Tag
  async getToday(daysBack = 7) {
    const res = await fetch(`${API_URL}/social/today?days_back=${daysBack}`, { headers: authHeaders() });
    return handle(res);
  },
  // Agent-Run MIT Schreibtisch-Kontext (ui_state). Eigene Methode hier, damit
  // morpheusClient.runSocial (shared, Edit-War-Gefahr) unangetastet bleibt.
  async runSocial(input, campaignId = "web-kreieren", uiState = null) {
    const res = await fetch(`${API_URL}/social/run`, {
      method: "POST",
      headers: authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify({ input, campaign_id: campaignId, ...(uiState ? { ui_state: uiState } : {}) }),
    });
    return handle(res);
  },
  // Live-Progress eines laufenden Agent-Runs (gleiche Status-Meldungen wie Telegram).
  // seq-Cursor: nur Events > after kommen zurück.
  async runStatus(thread, after = 0) {
    const res = await fetch(`${API_URL}/social/run-status?thread=${encodeURIComponent(thread)}&after=${after}`, { headers: authHeaders() });
    return handle(res);
  },
  // Modell-Umschalter (Einstellungen → Modell). Liest den Katalog llm_modelle.json
  // serverseitig; Keys kommen NIE ins Frontend, nur "bereit: true/false".
  async getLlm() {
    const res = await fetch(`${API_URL}/social/llm`, { headers: authHeaders() });
    return handle(res);
  },
  // Schreibt die .env und startet content-agent + kreieren-api neu (~5 s).
  // Bei laufendem Run kommt success:false zurück, statt ihn abzuwürgen.
  async setLlm(key, trotzdem = false) {
    const res = await fetch(`${API_URL}/social/llm`, {
      method: "POST",
      headers: authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify({ key, trotzdem }),
    });
    return handle(res);
  },
  // Kill-Switch: laufenden Agent-Run dieses Threads sauber abbrechen. Setzt nur
  // ein Flag — der Tool-Loop endet vor dem nächsten Schritt, "⏹ gestoppt" kommt
  // als normaler Turn zurück (kein Service-Restart mehr nötig).
  async stopRun(thread) {
    const res = await fetch(`${API_URL}/social/run-stop`, {
      method: "POST",
      headers: authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify({ thread }),
    });
    return handle(res);
  },
  // Mattis ✅/❌ in den LERN-Topf (reel_feedback). Läuft ZUSÄTZLICH zu Morpheus
  // /content/decision (Datei-Status) — der allein hat den Cutter nie erreicht,
  // Freigaben waren wochenlang lernblind (Befund 27.07.).
  async postReelDecision(payload) {
    const res = await fetch(`${API_URL}/social/reel-decision`, { method: "POST", headers: authHeaders({ "Content-Type": "application/json" }), body: JSON.stringify(payload) });
    return handle(res);
  },
  // Review-Seite (28.09.2026): Drafts, die auf Mattis Urteil warten — eigene Seite /review, nicht im Studio.
  async getReview() {
    const res = await fetch(`${API_URL}/social/review`, { headers: authHeaders() });
    return handle(res);
  },
  // entscheid: "freigabe" | "feedback" | "weg". Antwort kommt sofort, der Cutter arbeitet im Hintergrund.
  // keepalive: die Anfrage überlebt das Schließen der Seite (Freigabe darf nicht verloren gehen).
  async postReviewEntscheid(rid, entscheid, satz = "", keepalive = false) {
    const res = await fetch(`${API_URL}/social/review/entscheid`, { method: "POST", keepalive, headers: authHeaders({ "Content-Type": "application/json" }), body: JSON.stringify({ rid, entscheid, satz }) });
    return handle(res);
  },
  // Rückfrage nach der Freigabe (09.10.2026, Spec §3.2 A): Mattis Antwort wörtlich — Lob oder Kritik bucht der Server.
  // Pläne gesammelt auf /review (09.10.2026, Mattis Wahl 18:27): bauen (ein Tipp = sein Go) · weg · satz
  async postReviewPlan(rid, entscheid, satz = "") {
    const res = await fetch(`${API_URL}/social/review/plan`, { method: "POST", headers: authHeaders({ "Content-Type": "application/json" }), body: JSON.stringify({ rid, entscheid, satz }) });
    return handle(res);
  },
  // mehr (31.10.2026, Zwischenschritt nach Freigeben): { fassung: "v3" } bindet an die Frage dieser Fassung, { ueberspringen: true } legt sie weg.
  async postRueckblickAntwort(rid, satz, mehr = {}) {
    const res = await fetch(`${API_URL}/social/rueckblick/antwort`, { method: "POST", headers: authHeaders({ "Content-Type": "application/json" }), body: JSON.stringify({ rid, satz, ...mehr }) });
    return handle(res);
  },
  // Songtext-Karte der Review-Seite (28.09.2026): Musik der Datei, Korrektur in Musikersprache, Zurück, „Text stimmt“.
  songtextTonUrl(key, datei) {
    return `${API_URL}/social/songtext/ton?key=${encodeURIComponent(key)}&datei=${encodeURIComponent(datei)}&token=${encodeURIComponent(getToken() || "")}`;
  },
  async postSongtext(was, body) {
    const res = await fetch(`${API_URL}/social/songtext/${was}`, { method: "POST", headers: authHeaders({ "Content-Type": "application/json" }), body: JSON.stringify(body) });
    return handle(res);
  },
  // Sprachsteuerung: Audio-Blob → Whisper (gleicher Pfad wie Telegram-Voice) → Text
  async transcribe(blob, filename = "voice.webm") {
    const fd = new FormData();
    fd.append("file", blob, filename);
    const res = await fetch(`${API_URL}/social/transcribe`, { method: "POST", headers: authHeaders(), body: fd });
    return handle(res);
  },
};
